import {
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdErrorOutline, MdRefresh } from "react-icons/md";
import Card from "components/card/Card";
import { useOptimizeLive } from "../liveStore";

// The screen only has something to draw once the titles are loaded. Everything
// else (still loading, API down, partner has no eligible titles) is handled by
// <OptimizeStatus /> below.
export const useOptimizeReady = (): boolean =>
  useOptimizeLive((state) => state.mode === "live" && state.games.length > 0);

const OptimizeStatus: React.FC = () => {
  const mode = useOptimizeLive((s) => s.mode);
  const loadError = useOptimizeLive((s) => s.loadError);
  const init = useOptimizeLive((s) => s.init);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  if (mode === "loading")
    return (
      <Flex align="center" gap="12px" py="20px">
        <Spinner size="sm" />
        <Text color={subColor} fontSize="sm">
          Loading your titles…
        </Text>
      </Flex>
    );

  if (mode === "error")
    return (
      <Card p="24px" maxW="640px">
        <Flex align="center" mb="8px">
          <Icon as={MdErrorOutline} color="red.500" w="22px" h="22px" mr="10px" />
          <Text color={textColor} fontWeight="bold" fontSize="md">
            Couldn't load the Optimize data
          </Text>
        </Flex>
        <Text color={subColor} fontSize="sm" mb="4px">
          Your titles and their optimization states come straight from the API,
          so there is nothing to show until it answers.
        </Text>
        {loadError && (
          <Text color={subColor} fontSize="xs" mb="14px">
            {loadError}
          </Text>
        )}
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          variant="outline"
          borderRadius="12px"
          size="sm"
          onClick={() => init(true)}
        >
          Retry
        </Button>
      </Card>
    );

  // loaded, but the partner has no titles the backend considers eligible
  return (
    <Box>
      <Text color={textColor} fontWeight="bold" fontSize="md" mb="4px">
        No titles yet
      </Text>
      <Text color={subColor} fontSize="sm">
        None of your games are set up for optimizations yet. Once a title is on
        file it shows up here, per platform.
      </Text>
    </Box>
  );
};

export default OptimizeStatus;
